import React from 'react';

import { ActionTabs, type ActionTabItem, AppIcon, appStrings } from '@backrs_ui/common-ui';

import {
  GuideCallout,
  GuideDefinitionStrip,
  GuideMarkdown,
  guideMarkdownToText,
  GuideToolsSection,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { CollegeApplicationRoadmapMonth } from '@backrs_ui/grow-ui/src/pages/CollegeApplicationRoadmapGuide/content';
import { useGetTaskActionsAndProperties } from '@backrs_ui/grow-ui/src/pages/Priorities/hooks/tasks.hooks';
import { openSignupPrompt } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { RoadmapPeriodBadge } from './RoadmapPeriodBadge';

/** Replaced with the month's period label so the focus heading reads 'This April, focus on'. */
const MONTH_PLACEHOLDER = '{month}';

type RoadmapMonthDetailProps = Readonly<{
  month: CollegeApplicationRoadmapMonth;
  focusTitle: string;
  toolsTitle: string;
}>;

export const RoadmapMonthDetail: React.FC<RoadmapMonthDetailProps> = ({
  month,
  focusTitle,
  toolsTitle,
}) => {
  const { handleOpenNewTaskDialog } = useGetTaskActionsAndProperties();
  const { loggedIn } = useAuth();
  const dispatch = useAppDispatch();

  // The task dialog only mounts for signed-in users, so guests get the signup prompt instead.
  const focusActions: ActionTabItem[] = [
    {
      id: `college-application-roadmap-${month.id}-add-task-btn`,
      label: appStrings.modules.actionTabs.task,
      icon: 'BookmarkAdd',
      onClick: () => {
        if (!loggedIn) {
          dispatch(openSignupPrompt());

          return;
        }

        handleOpenNewTaskDialog(null, false, {
          title: `${month.period.label}: ${month.title}`,
          description: month.focus.map((item) => `- ${guideMarkdownToText(item)}`).join('\n'),
        });
      },
    },
  ];

  return (
    <Box sx={containerSx}>
      <Box sx={headerSx}>
        <RoadmapPeriodBadge label={month.period.label} caption={month.period.caption} />
        <Typography variant="display-xs-semibold" component="h2" sx={titleSx}>
          {month.title}
        </Typography>
      </Box>

      {month.definitions && <GuideDefinitionStrip definitions={month.definitions} />}

      <Box sx={bodySx}>
        {month.body.map((paragraph) => (
          <GuideMarkdown key={paragraph}>{paragraph}</GuideMarkdown>
        ))}
      </Box>

      <Box sx={focusSx}>
        <Box sx={focusHeaderSx}>
          <Typography variant="text-md-semibold">
            {focusTitle.replace(MONTH_PLACEHOLDER, month.period.label)}
          </Typography>
          <ActionTabs actions={focusActions} />
        </Box>
        <Box sx={focusListSx}>
          {month.focus.map((item) => (
            <Box key={item} sx={focusItemSx}>
              <Box sx={focusIconSx}>
                <AppIcon
                  iconName="Check"
                  diameter="16px"
                  color="Colors/Foreground/fg-brand-primary"
                />
              </Box>
              <GuideMarkdown variant="text-sm-regular">{item}</GuideMarkdown>
            </Box>
          ))}
        </Box>
      </Box>

      {month.callout && (
        <GuideCallout
          variant={month.callout.variant}
          title={month.callout.title}
          body={month.callout.body}
        />
      )}

      <Box sx={toolsSx}>
        <Typography variant="text-sm-semibold" sx={toolsTitleSx}>
          {toolsTitle}
        </Typography>
        <GuideToolsSection
          toolIds={month.toolIds}
          nuggetSlugs={month.nuggetSlugs}
          contextLabel={`the ${month.period.label} row of the college application roadmap`}
        />
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  m: backrs.spacing['spacing-none'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const focusSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  p: backrs.spacing['spacing-2xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
});

const focusHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-lg'],
  flexWrap: 'wrap',
});

const focusListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const focusItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
});

const focusIconSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  height: '20px',
  flex: '0 0 auto',
};

const toolsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const toolsTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
